import React, { useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { Menu, MenuItem } from '@mui/material';
import html2pdf from 'html2pdf.js';

import { useTranslation } from 'react-i18next';
import { useGetStatsData } from '../../hooks/use-get-stats-data';
import { useStatsFiltersContext } from '../../hooks/use-stats-filters';
import { DatePicker } from '@/components/design-system/molecules/datepicker';
import { StatsCitySelectionDropDown } from '../stats-city-selection-dropdown';
import { StatsHouseSelectionDropDown } from '../stats-house-selection-dropdown';
import { StatsPropertyManagerSelectionDropDown } from '../stats-property-manager-selection-dropdown';
import { StatsOwnerSelectionDropDown } from '../stats-owner-selection-dropdown';
import { StatCard } from '../stat-card';
import { useDateRange } from '../../hooks/use-date-range';
import { FinancialChart } from '../financial-chart';
import { OccupancyPaymentsChart } from '../occupancy-payments-chart';
import { HousesAndRoomsCard } from '../houses-and-rooms-card';
import { RentalTrendsChart } from '../rental-trends-chart';
import { MapOverview } from '../map-overview';
import { VacancyMaintenanceOverview } from '../vacancy-maintenance-overview';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';

interface StatValue {
  value: string | number;
  label: string;
  path?: string;
  direction?: 'up' | 'down';
  prev_value?: string | number;
  prev_period?: string;
  description?: string;
  verdict?: 'good' | 'bad';
}

interface StatBlock {
  vacancy_days?: StatValue;
  days_to_rent?: StatValue;
  average_stay?: StatValue;
}

interface UserStats {
  new_users?: StatValue;
  new_applicants?: StatValue;
  activated_contracts?: StatValue;
  activated_room_swaps?: StatValue;
  acceptance_rate?: StatValue;
  conversion_rate?: StatValue;
}

const StatsDashboard = () => {
  const { startDate, endDate, cityId, selectedHouse, propertyManagerId, selectedOwner, setStartDate, setEndDate } = useStatsFiltersContext();
  const { data: statsData } = useGetStatsData();
  const { handleDateRange } = useDateRange({ setStartDate, setEndDate });
  const dashboardRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const Markers = statsData?.data?.data?.colocation_markers || [];
  const userStats: UserStats = statsData?.data?.data?.users_block || {};
  const statBlock: StatBlock = statsData?.data?.data?.stat_block || {};
  const houseAndRooms = statsData?.data?.data?.house_and_rooms || {};
  const rentalTrends = statsData?.data?.data?.rental_overview || {};
  const financialData = statsData?.data?.data?.financial || {
    rent: [],
    charges: [],
    collected: [],
    x_axis: [],
  };

  const occupancyData = statsData?.data?.data?.occupancy_payments || {
    data: [0, 0], // default values if data is missing
  };

  // Transform the data to match the expected format
  const transformedOccupancyData = {
    occupancy: occupancyData.data?.[0] || 0,
    payments: occupancyData.data?.[1] || 0,
  };

  const { t } = useTranslation();

  const handleExportPDF = () => {
    handleClose();

    const mapContainer = document.querySelector('.map-overview-container');
    if (mapContainer) {
      mapContainer.scrollIntoView({ behavior: 'smooth' });
    }
    setTimeout(() => {
      const element = dashboardRef.current;

      // Add PDF-specific classes before generating
      const vacancyOverviews = element?.querySelectorAll('.vacancy-maintenance-overview');
      vacancyOverviews?.forEach((overview) => {
        overview.classList.add('pdf-export');
      });

      // Add a specific class to the map container for PDF export
      const mapContainer = element?.querySelector('.map-overview-container');
      if (mapContainer) {
        mapContainer.classList.add('pdf-map-container');
      }

      if (element) {
        element.style.backgroundColor = '#F9F9FA';

        // Add padding wrapper for PDF export
        const originalPadding = element.style.padding;
        element.style.padding = '5px'; // Adjust this value as needed

        // Add timestamp element before generating PDF
        const timestampDiv = document.createElement('div');
        timestampDiv.className =
          'text-md w-full border-t border-solid border-[#eee] pb-1 pt-2 text-right text-[#666666]';
        timestampDiv.innerHTML = `${t(
          'Exported on'
        )}: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
        element.appendChild(timestampDiv);

        const opt = {
          margin: 0.25, // External margins
          filename: 'stats-dashboard.pdf',
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: {
            scale: 1.5,
            logging: false,
            useCORS: true,
            windowWidth: 1920,
            backgroundColor: '#F9F9FA',
            padding: 0,
          },
          jsPDF: {
            unit: 'in',
            format: [24, 14],
            orientation: 'portrait',
            compress: true,
          },
          pagebreak: {
            mode: 'css',
            avoid: ['.stats-dashboard-content', '.map-overview-container'],
          },
        };

        html2pdf()
          .set(opt)
          .from(element)
          .save()
          .then(() => {
            // Cleanup
            vacancyOverviews?.forEach((overview) => {
              overview.classList.remove('pdf-export');
            });
            if (mapContainer) {
              mapContainer.classList.remove('pdf-map-container');
            }
            element.removeChild(timestampDiv);

            // Restore original padding
            element.style.padding = originalPadding;

            // Instantly scroll back to top after export
            window.scrollTo({ top: 0 });
          });
      }
    }, 1500); // Slightly longer delay for smooth scroll
  };

  const handleDownloadXLSX = () => {
    handleClose();

    const params = new URLSearchParams();

    if (startDate) params.append('from', startDate);
    if (endDate) params.append('to', endDate);
    if (cityId) params.append('city_id', cityId.toString());
    if (selectedHouse?.id) params.append('colocation_id', selectedHouse.id.toString());
    if (propertyManagerId) params.append('property_manager_id', propertyManagerId.toString());
    if (selectedOwner?.id) params.append('owner_id', selectedOwner.id.toString());

    window.open(`/api/v1/admin/stats_dashboard.xlsx?${params.toString()}`, '_blank');
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Controls - Not included in PDF */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
        <h1 className="text-text-primary-heavy font-Vollkorn text-[25px] font-bold leading-[32px]">
          {t('js.features.admin.stats.dashboard.title')}
        </h1>
        <button
          onClick={handleClick}
          className="flex h-[40px] w-[128px] items-center justify-center gap-1 rounded-[8px] bg-R300 font-sans text-[14px] font-medium leading-[20px] text-[#212529] hover:bg-R400 disabled:bg-E50 disabled:text-E75"
        >
          <IconSaxIcon name="arrow-top-right-on-square" size={16} strokeWidth={1.5} />
          {t('Export')}
          <IconSaxIcon name="arrow-down" size={16} strokeWidth={1.5} />
        </button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #E5E7EB',
              minWidth: '180px',
              padding: '0px',
              '& .MuiList-root': {
                padding: '8px',
              }
            }
          }}
        >
          <MenuItem 
            onClick={handleExportPDF}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              '&:hover': {
                backgroundColor: 'rgba(249, 250, 251, 1)',
              }
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconSaxIcon name="document-text" size={14} strokeWidth={1.5} color="white" />
            </Box>
            {t('Export as PDF')}
          </MenuItem>
          
          <MenuItem 
            onClick={handleDownloadXLSX}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              '&:hover': {
                backgroundColor: '#F9FAFB',
              }
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconSaxIcon name="document-download" size={14} strokeWidth={1.5} color="white" />
            </Box>
            {t('Export as XLSX')}
          </MenuItem>
        </Menu>
      </Box>

      {/* Filters - Not included in PDF */}
      <Grid
        container
        spacing={1}
        sx={{
          mb: 2,
          '& .MuiGrid-item': {
            width: { xs: '100%', sm: '50%', md: '25%' },
            minWidth: { md: '170px' },
            maxWidth: { md: '215px' },
          },
        }}
        className="flex flex-wrap"
      >
        <Grid item>
          <StatsCitySelectionDropDown />
        </Grid>
        <Grid item>
          <StatsHouseSelectionDropDown />
        </Grid>
        <Grid item>
          <StatsPropertyManagerSelectionDropDown />
        </Grid>
        <Grid item>
          <StatsOwnerSelectionDropDown />
        </Grid>
        <Grid item>
          <DatePicker
            className="w-full"
            onChange={handleDateRange}
            label="Select date"
            dateRange
            type="month"
            startValue={startDate ? new Date(startDate) : undefined}
            endValue={endDate ? new Date(endDate) : undefined}
            placeholder="Select date range"
          />
        </Grid>
      </Grid>

      {/* Content to be included in PDF */}
      <Box ref={dashboardRef} className="stats-dashboard-content">
        {/* Add class for stats grid section */}
        <Box className="stats-grid-section">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="new-user"
                value={userStats.new_users?.value}
                label={userStats.new_users?.label || 'New Users'}
                path={userStats.new_users?.path || '#'}
                direction={userStats.new_users?.direction}
                prevValue={userStats.new_users?.prev_value}
                prevPeriod={userStats.new_users?.prev_period}
                verdict={userStats.new_users?.verdict}
                description={userStats.new_users?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="large-applications"
                value={userStats.new_applicants?.value}
                label={userStats.new_applicants?.label || 'Applications'}
                path={userStats.new_applicants?.path || '#'}
                direction={userStats.new_applicants?.direction}
                prevValue={userStats.new_applicants?.prev_value}
                prevPeriod={userStats.new_applicants?.prev_period}
                verdict={userStats.new_applicants?.verdict}
                description={userStats.new_applicants?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="large-applications"
                value={userStats.activated_contracts?.value}
                label={userStats.activated_contracts?.label || 'Activated Contracts'}
                path={userStats.activated_contracts?.path || '#'}
                direction={userStats.activated_contracts?.direction}
                prevValue={userStats.activated_contracts?.prev_value}
                prevPeriod={userStats.activated_contracts?.prev_period}
                verdict={userStats.activated_contracts?.verdict}
                description={userStats.activated_contracts?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="active-rooms"
                value={userStats.activated_room_swaps?.value}
                label={userStats.activated_room_swaps?.label || 'Active Room Swaps'}
                path={userStats.activated_room_swaps?.path || '#'}
                direction={userStats.activated_room_swaps?.direction}
                prevValue={userStats.activated_room_swaps?.prev_value}
                prevPeriod={userStats.activated_room_swaps?.prev_period}
                verdict={userStats.activated_room_swaps?.verdict}
                description={userStats.activated_room_swaps?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="check-rates"
                value={userStats.acceptance_rate?.value}
                label={userStats.acceptance_rate?.label || 'Acceptance Rate'}
                direction={userStats.acceptance_rate?.direction}
                prevValue={userStats.acceptance_rate?.prev_value}
                prevPeriod={userStats.acceptance_rate?.prev_period}
                verdict={userStats.acceptance_rate?.verdict}
                description={userStats.acceptance_rate?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="stats-home"
                value={userStats.conversion_rate?.value}
                label={userStats.conversion_rate?.label || 'Conversion Rate'}
                direction={userStats.conversion_rate?.direction}
                prevValue={userStats.conversion_rate?.prev_value}
                prevPeriod={userStats.conversion_rate?.prev_period}
                verdict={userStats.conversion_rate?.verdict}
                description={userStats.conversion_rate?.description}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="large-applications"
                value={statBlock.vacancy_days?.value}
                label={statBlock.vacancy_days?.label || 'Vacancy'}
                displayDays
                direction={statBlock.vacancy_days?.direction}
                prevValue={statBlock.vacancy_days?.prev_value}
                prevPeriod={statBlock.vacancy_days?.prev_period}
                description={statBlock.vacancy_days?.description}
                verdict={statBlock.vacancy_days?.verdict}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="large-applications"
                value={statBlock.days_to_rent?.value}
                label={statBlock.days_to_rent?.label || 'Re-rent'}
                displayDays
                direction={statBlock.days_to_rent?.direction}
                prevValue={statBlock.days_to_rent?.prev_value}
                prevPeriod={statBlock.days_to_rent?.prev_period}
                description={statBlock.days_to_rent?.description}
                verdict={statBlock.days_to_rent?.verdict}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="stats-calendar"
                value={statBlock.average_stay?.value}
                label={statBlock.average_stay?.label || 'Average Stay'}
                displayDays
                direction={statBlock.average_stay?.direction}
                prevValue={statBlock.average_stay?.prev_value}
                prevPeriod={statBlock.average_stay?.prev_period}
                description={statBlock.average_stay?.description}
                verdict={statBlock.average_stay?.verdict}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="repair"
                value={houseAndRooms.days_in_maintenance?.value}
                label="Total maintenance"
                displayDays
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <StatCard
                icon="rocket"
                value={statBlock.launch_house?.value}
                label="To launch a house"
                displayDays
              />
            </Grid>
          </Grid>
        </Box>

        {/* Add class for charts section */}
        <Box className="stats-charts-section" sx={{ width: '100%', mt: 3 }}>
          <Grid container spacing={3}>
            {/* Left Column - Vacancy Overview */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                '& > div': {
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <VacancyMaintenanceOverview />
            </Grid>

            {/* Right Column */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                order: { xs: 2, lg: 1 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  width: '100%',
                  height: '100%',
                }}
              >
                {/* Financial Chart */}
                <Box sx={{ width: '100%' }}>
                  <FinancialChart data={financialData} />
                </Box>

                {/* Houses & Rooms and Occupancy Section */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '2fr 1fr' },
                    gap: 3,
                    width: '100%',
                  }}
                >
                  <HousesAndRoomsCard data={houseAndRooms} />
                  <OccupancyPaymentsChart data={transformedOccupancyData} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ width: '100%', mt: 3 }}>
          <RentalTrendsChart
            data={{
              newRentals: rentalTrends.rental_data || [],
              reRentals: rentalTrends.re_rental_data || [],
              newRentalLinks: rentalTrends.rental_links || [],
              reRentalLinks: rentalTrends.re_rental_links || [],
              months: rentalTrends.x_axis || [],
            }}
          />
        </Box>

        <Box sx={{ width: '100%', mt: 3, mb: 5 }} className="map-overview-container">
          <MapOverview markers={Markers} />
        </Box>
      </Box>
    </Box>
  );
};

export default StatsDashboard;
